import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Switch,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ais-ui/ui-core';
import { UseFormReturn } from 'react-hook-form';
import { cn } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { MapFormValues } from './useMapForm';
import { TargetHoverLabelForm } from './TargetHoverLabelForm';

interface MapFormProps {
  form: UseFormReturn<MapFormValues>;
}

export function MapForm({ form }: MapFormProps) {
  const { messages } = useLocalization();
  const enableTrack = form.watch('enable_track');

  // Define available projections
  const availableProjections = [
    { value: 'EPSG:3857', label: '3857 (Web Mercator)' },
  ];

  return (
    <Form {...form}>
      <div className="flex flex-col space-y-12">
        {/* Projection Section - Only show if more than 1 projection available */}
        {availableProjections.length > 1 && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Map Projection</h3>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="displayProjection"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Display Projection
                      </FormLabel>
                      <FormDescription>
                        Choose the map projection system for displaying
                        coordinates and data
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Select projection" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableProjections.map((proj) => (
                            <SelectItem key={proj.value} value={proj.value}>
                              {proj.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        )}

        {/* Track Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">
            {messages.settingsMapTrackTitle}
          </h3>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="enable_track"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapTrackEnableTrackLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapTrackEnableTrackDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="history_in_hours"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4',
                    !enableTrack && 'opacity-50'
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapTrackHistoryLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapTrackHistoryDescription}
                    </FormDescription>
                  </div>
                  <FormControl onChange={() => form.clearErrors()}>
                    <Input
                      className="w-20"
                      disabled={!enableTrack}
                      type="number"
                      step="1"
                      {...field}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange('');
                        } else {
                          const numberValue = parseInt(e.target.value, 10);
                          field.onChange(numberValue);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="show_samples"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4',
                    !enableTrack && 'opacity-50'
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapTrackSamplesLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapTrackSamplesDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      disabled={!enableTrack}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="show_direction_arrows"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4',
                    !enableTrack && 'opacity-50'
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapTrackDirectionalArrowsLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapTrackDirectionalArrowsDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      disabled={!enableTrack}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="interval_in_minutes"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4',
                    !enableTrack && 'opacity-50'
                  )}
                >
                  <div className="space-y-0.5 mr-2">
                    <FormLabel className="text-base">
                      {messages.settingsMapTrackIntervalLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapTrackIntervalDescription}
                    </FormDescription>
                  </div>
                  <FormControl onChange={() => form.clearErrors()}>
                    <Input
                      className="w-20"
                      disabled={!enableTrack}
                      type="number"
                      step="1"
                      {...field}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange('');
                        } else {
                          const numberValue = parseInt(e.target.value, 10);
                          field.onChange(numberValue);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-4 border rounded-lg p-4 w-full">
              <div>
                <div className="text-base font-medium">Position jumps</div>
                <p className="text-sm text-muted-foreground">
                  Maximum distance or speed a target can jump before being
                  filtered out
                </p>
              </div>

              <div className="flex space-x-2 w-full">
                <FormField
                  control={form.control}
                  name="track_max_position_jump_in_meters"
                  render={({ field }) => (
                    <FormItem
                      className={cn(
                        'flex flex-row items-center justify-between border rounded-lg p-4 w-full',
                        !enableTrack && 'opacity-50'
                      )}
                    >
                      <div className="space-y-0.5">
                        <FormLabel className="text-sm">
                          Maximum distance
                        </FormLabel>
                        <FormDescription>Distance in meters</FormDescription>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <Input
                          className="w-20"
                          disabled={!enableTrack}
                          type="number"
                          {...field}
                          onChange={(e) => {
                            if (!e.target.value) {
                              field.onChange('');
                            } else {
                              const numberValue = parseFloat(e.target.value);
                              field.onChange(numberValue);
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="track_max_position_jump_in_knots"
                  render={({ field }) => (
                    <FormItem
                      className={cn(
                        'flex flex-row items-center justify-between border rounded-lg p-4 w-full',
                        !enableTrack && 'opacity-50'
                      )}
                    >
                      <div className="space-y-0.5">
                        <FormLabel className="text-sm">Maximum speed</FormLabel>
                        <FormDescription>Speed in knots</FormDescription>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <Input
                          className="w-20"
                          disabled={!enableTrack}
                          type="number"
                          {...field}
                          onChange={(e) => {
                            if (!e.target.value) {
                              field.onChange('');
                            } else {
                              const numberValue = parseFloat(e.target.value);
                              field.onChange(numberValue);
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Target Hover Label Section */}
        <TargetHoverLabelForm form={form} />

        {/* Labels Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">
            {messages.settingsMapLabelTitle}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="labelZoomLevel"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelZoomLevelLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelZoomLevelDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="zoom"
                      className="w-20"
                      min={0}
                      max={22}
                      {...field}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange('');
                        } else {
                          let numberValue = parseInt(e.target.value);
                          if (numberValue < 0) numberValue = 0;
                          if (numberValue > 22) numberValue = 22;
                          field.onChange(numberValue);
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="labelZoomLevelFilter"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelZoomLevelFilterLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelZoomLevelFilterDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="zoom"
                      min={0}
                      max={22}
                      className="w-20"
                      {...field}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange('');
                        } else {
                          let numberValue = parseInt(e.target.value);
                          if (numberValue < 0) numberValue = 0;
                          if (numberValue > 22) numberValue = 22;
                          field.onChange(numberValue);
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Label boolean fields */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelNameLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelNameDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="mmsi"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelMmsiLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelMmsiDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="aisType"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelAisTypeLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelAisTypeDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="callSign"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelCallsignLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelCallsignDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Continue with all other label fields... */}
            <FormField
              control={form.control}
              name="imo"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelImoLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelImoDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="typeOfShip"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelTypeOfShipLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelTypeOfShipDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="typeOfCargo"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelTypeOfCargoLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelTypeOfCargoDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelCountryLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelCountryDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="destination"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelDestinationLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelDestinationDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="size"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelSizeLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelSizeDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="speedOverGround"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelSpeedOverGroundLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelSpeedOverGroundDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="courseOverGround"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelCourseOverGroundLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelCourseOverGroundDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="heading"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelHeadingLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelHeadingDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="draught"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelDraughtLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelDraughtDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="navigationalStatus"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapLabelNavigationalStatusLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapLabelNavigationalStatusDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
    </Form>
  );
}
